/** Extract a human-readable message from an error object.
 *
 * Priority:
 * 1. Error payload fields (`msg`, `message`, `error`, `detail`) on
 *    Axios `response.data`, plain `{ data }`, or the object itself
 *    (e.g. RTK `rejectWithValue` payload)
 * 2. Native `Error.message` when it is not a generic Axios status line
 * 3. A plain rate-limit sentence for HTTP 429 when the body is HTML or empty
 * 4. `defaultErrorMessage` ("Something went wrong")
 *
 * HTML documents are ignored. A 429 from a gateway is often a full page, and
 * putting that string in a toast shows the markup.
 */
const PAYLOAD_MESSAGE_KEYS = [
    'msg',
    'message',
    'error_message',
    'error',
    'detail',
    'BadRequest',
    'NotAuthorisedError',
] as const;

const isNonEmptyString = (value: unknown): value is string =>
    typeof value === 'string' && value.trim().length > 0;

/** Axios default messages that hide the real server payload. */
const isGenericHttpErrorMessage = (message: string): boolean =>
    /^Request failed with status code \d+$/i.test(message) ||
    message === 'Network Error' ||
    message === 'Failed to fetch';

/**
 * Gateways (nginx, Azure, Cloudflare) answer 429 with an HTML page.
 * That string is not a message — the toast would show the markup.
 */
const isMarkupMessage = (value: string): boolean =>
    /^<!doctype\s+html/i.test(value) ||
    /^<html[\s>]/i.test(value) ||
    /<(?:html|head|body|title|div|p|h1|center|style|script)\b/i.test(value);

const asUsableMessage = (value: string): string | null => {
    const trimmed = value.trim();
    if (!trimmed || isMarkupMessage(trimmed)) {
        return null;
    }
    return trimmed;
};

const RATE_LIMIT_MESSAGE = 'Too many requests. Please try again later.';

const pickMessageFromPayload = (payload: unknown): string | null => {
    if (isNonEmptyString(payload)) {
        return asUsableMessage(payload);
    }

    if (!payload || typeof payload !== 'object') {
        return null;
    }

    const data = payload as Record<string, unknown>;

    for (const key of PAYLOAD_MESSAGE_KEYS) {
        const value = data[key];
        const usable = isNonEmptyString(value) ? asUsableMessage(value) : null;
        if (usable) {
            return usable;
        }
    }

    // Backend validation list: { ValidationError: [{ field, msg }] }
    const validationError = data.ValidationError;
    if (Array.isArray(validationError) && validationError.length > 0) {
        const parts = validationError
            .map((item) => {
                if (!item || typeof item !== 'object') return null;
                const entry = item as { field?: unknown; msg?: unknown };
                const msg = isNonEmptyString(entry.msg) ? asUsableMessage(entry.msg) : null;
                if (!msg) return null;
                const field = Array.isArray(entry.field)
                    ? entry.field.filter(isNonEmptyString).join('.')
                    : '';
                return field ? `${field}: ${msg}` : msg;
            })
            .filter((part): part is string => Boolean(part));
        if (parts.length) {
            return parts.join('; ');
        }
    }

    // Validation-style: { errors: ["..."] } or { errors: [{ msg/message }] }
    const errors = data.errors;
    if (Array.isArray(errors) && errors.length > 0) {
        const parts = errors
            .map((item) => {
                if (isNonEmptyString(item)) return asUsableMessage(item);
                if (item && typeof item === 'object') {
                    return pickMessageFromPayload(item);
                }
                return null;
            })
            .filter((part): part is string => Boolean(part));
        if (parts.length) {
            return parts.join(', ');
        }
    }

    return null;
};

const getPayloadCandidates = (error: object): unknown[] => {
    const candidates: unknown[] = [];

    // Axios-style: error.response.data
    if ('response' in error) {
        candidates.push((error as { response?: { data?: unknown } }).response?.data);
    }

    // Plain: error.data
    if ('data' in error) {
        candidates.push((error as { data?: unknown }).data);
    }

    // RTK rejectWithValue / already-unwrapped payload object
    candidates.push(error);

    return candidates;
};

/** HTTP status of an Axios-style error, or `null` when it is not an HTTP failure. */
export const getErrorStatus = (error: unknown): number | null => {
    if (!error || typeof error !== 'object' || !('response' in error)) {
        return null;
    }
    const status = (error as { response?: { status?: unknown } }).response?.status;
    return typeof status === 'number' ? status : null;
};

export const getErrorMessage = (
    error: unknown,
    defaultErrorMessage = 'Something went wrong'
): string => {
    if (error == null) {
        return defaultErrorMessage;
    }

    if (isNonEmptyString(error)) {
        return asUsableMessage(error) ?? defaultErrorMessage;
    }

    if (typeof error === 'object') {
        for (const candidate of getPayloadCandidates(error)) {
            const fromPayload = pickMessageFromPayload(candidate);
            // Skip Axios's own "Request failed with status code 429" so an
            // HTML body does not fall through to that status line.
            if (fromPayload && !isGenericHttpErrorMessage(fromPayload)) {
                return fromPayload;
            }
        }

        if (error instanceof Error && isNonEmptyString(error.message)) {
            const message = asUsableMessage(error.message);
            if (message && !isGenericHttpErrorMessage(message)) {
                return message;
            }
        }

        // HTML (or an empty) 429 body has nothing safe to show.
        if (getErrorStatus(error) === 429) {
            return RATE_LIMIT_MESSAGE;
        }
    }

    return defaultErrorMessage;
};
